import { v4 as uuid } from 'uuid';
import { deleteOwnedMedia, uploadOwnedMedia } from '../../util/ownedMedia';

function isUsableFile(file) {
    if (!file) return false;
    if (typeof File !== 'undefined') {
        return file instanceof File && file.size > 0;
    }
    return typeof file === 'object' && Number(file.size) > 0;
}

function normalizePaidEquipmentItem(item, index, id) {
    const price = Number.isFinite(Number.parseFloat(item?.pricePerPiece))
        ? Number.parseFloat(item.pricePerPiece)
        : 0;
    const quantity = Number.isFinite(Number.parseInt(item?.quantity, 10))
        ? Number.parseInt(item.quantity, 10)
        : 1;

    return {
        id,
        category: item?.category || '',
        name: item?.nameModel?.trim() || '',
        price,
        quantity,
        description: (item?.description || '').trim() || null,
        imageKey: item?.imageKey || null,
        sortOrder: index,
    };
}

function isValidPaidEquipmentItem(item) {
    return Boolean(item.category && item.name && item.price > 0 && item.quantity > 0);
}

function addUploadedMedia(uploadedMedia, media) {
    uploadedMedia.push(media);
    return media.key;
}

export async function prepareStudioCreationOwnedMedia({
    studioId,
    images,
    paidEquipmentSource,
    uploadMedia = uploadOwnedMedia,
    idGenerator = uuid,
    uploadedMedia = [],
}) {
    if (!studioId) {
        throw new Error('Studio konnte nicht zugeordnet werden.');
    }

    const imageKeys = [];
    const paidEquipmentItems = [];

    for (const image of Array.isArray(images) ? images : []) {
        const file = image?.file;
        if (!isUsableFile(file)) {
            throw new Error('Bilddatei ist nicht mehr verfügbar. Bitte lade die Fotos erneut hoch.');
        }

        const uploadResult = await uploadMedia({
            file,
            entityType: 'STUDIO',
            entityId: studioId,
            mediaKind: 'IMAGE',
            slot: 'gallery',
            fileName: file.name || 'studio-photo.jpg',
        });

        imageKeys.push(addUploadedMedia(uploadedMedia, {
            key: uploadResult.key,
            entityType: 'STUDIO',
            entityId: studioId,
            mediaKind: 'IMAGE',
        }));
    }

    for (const [index, item] of (Array.isArray(paidEquipmentSource) ? paidEquipmentSource : []).entries()) {
        const equipmentItemId = idGenerator();
        const normalizedItem = normalizePaidEquipmentItem(item, index, equipmentItemId);

        if (!isValidPaidEquipmentItem(normalizedItem)) {
            continue;
        }

        const imageFile = item?.image?.file;
        if (isUsableFile(imageFile)) {
            const uploadResult = await uploadMedia({
                file: imageFile,
                entityType: 'PAID_EQUIPMENT',
                entityId: equipmentItemId,
                parentEntityId: studioId,
                mediaKind: 'IMAGE',
                slot: 'equipment',
                fileName: imageFile.name || 'equipment.jpg',
            });

            normalizedItem.imageKey = addUploadedMedia(uploadedMedia, {
                key: uploadResult.key,
                entityType: 'PAID_EQUIPMENT',
                entityId: equipmentItemId,
                parentEntityId: studioId,
                mediaKind: 'IMAGE',
            });
        }

        paidEquipmentItems.push(normalizedItem);
    }

    return {
        imageKeys,
        paidEquipmentItems,
        uploadedMedia,
    };
}

export async function cleanupStudioCreationUploadedMedia({
    uploadedMedia,
    deleteMedia = deleteOwnedMedia,
}) {
    const uniqueMedia = [];
    const seen = new Set();

    for (const media of uploadedMedia || []) {
        const key = [
            media?.key,
            media?.entityType,
            media?.entityId,
            media?.parentEntityId || '',
            media?.mediaKind,
        ].join('|');

        if (!media?.key || seen.has(key)) continue;
        seen.add(key);
        uniqueMedia.push(media);
    }

    if (uniqueMedia.length < 1) {
        return null;
    }

    const cleanupResults = await Promise.allSettled(
        uniqueMedia.map((media) => deleteMedia(media))
    );

    return cleanupResults.find((result) => result.status === 'rejected')?.reason || null;
}

export async function publishStudioCreationWithOwnedMedia({
    studioId,
    images,
    paidEquipmentSource,
    buildStudioData,
    handleStudioCreation,
    uploadMedia = uploadOwnedMedia,
    deleteMedia = deleteOwnedMedia,
    idGenerator = uuid,
}) {
    const resolvedStudioId = studioId || idGenerator();
    const uploadedMedia = [];

    try {
        const preparedMedia = await prepareStudioCreationOwnedMedia({
            studioId: resolvedStudioId,
            images,
            paidEquipmentSource,
            uploadMedia,
            idGenerator,
            uploadedMedia,
        });

        const studioDataForBackend = buildStudioData({
            studioId: resolvedStudioId,
            imageKeys: preparedMedia.imageKeys,
            paidEquipmentItems: preparedMedia.paidEquipmentItems,
        });

        return await handleStudioCreation(studioDataForBackend);
    } catch (error) {
        const cleanupError = await cleanupStudioCreationUploadedMedia({
            uploadedMedia,
            deleteMedia,
        });

        if (cleanupError) {
            error.cleanupError = cleanupError;
        }

        throw error;
    }
}
